/**
 * Status vocabulary. One shape, one colour system, text always present —
 * colour never carries the meaning on its own.
 */
import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { Icon } from '../icons';

export type Tone = 'ok' | 'warn' | 'danger' | 'accent' | 'neutral' | 'info' | 'solid';

export function Badge({ tone = 'neutral', dot, live, title, children }: {
  tone?: Tone;
  /** Leading dot — use for lifecycle states (QUEUED/RUNNING/DONE). */
  dot?: boolean;
  /** Pulses the dot: something is happening right now. */
  live?: boolean;
  title?: string;
  children: ReactNode;
}) {
  return (
    <span className={cx('badge', tone, dot && 'dot', live && 'live')} title={title}>
      {children}
    </span>
  );
}

/** A removable filter chip. */
export function Chip({ children, onRemove }: { children: ReactNode; onRemove?: () => void }) {
  return (
    <span className="chip">
      {children}
      {onRemove && (
        <button type="button" onClick={onRemove} aria-label="Remove filter">
          <Icon.Close aria-hidden />
        </button>
      )}
    </span>
  );
}

/** The dot-separated metadata line under a title. Falsy children are dropped. */
export function Meta({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('meta', className)}>{children}</div>;
}

/** Where a datum came from — the brand promise of a legal product. */
export function Provenance({ source, asOf, children }: { source?: ReactNode; asOf?: ReactNode; children?: ReactNode }) {
  return (
    <span className="provenance">
      <Icon.Shield aria-hidden />
      {source && <span className="src">{source}</span>}
      {asOf && <span>as of {asOf}</span>}
      {children}
    </span>
  );
}

/** Keyboard hint. `keys` is rendered verbatim, e.g. ['⌘', 'K']. */
export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="kbd">{children}</kbd>;
}
